/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueErrorSnackbar } from 'actions/Notifications';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchEmptyQuerySettings = createAsyncThunk(
  'emptyQuery/fetchSettings',
  async (scope = 'default', { extra, dispatch, rejectWithValue }) => {
    try {
      const { emptyQueryApi } = extra.serviceRegistry;
      if (!emptyQueryApi) {
        return rejectWithValue('emptyQueryApi not available on current transport');
      }
      return await emptyQueryApi.getSettings(scope);
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('emptyQuery.settings.loadFailed'),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const saveEmptyQuerySettings = createAsyncThunk(
  'emptyQuery/saveSettings',
  async (payload, { extra, dispatch, rejectWithValue }) => {
    try {
      const { emptyQueryApi } = extra.serviceRegistry;
      if (!emptyQueryApi) {
        return rejectWithValue('emptyQueryApi not available on current transport');
      }
      const scopedPayload = payload && Object.prototype.hasOwnProperty.call(payload, 'settings')
        ? payload
        : { settings: payload, scope: 'default' };
      return await emptyQueryApi.saveSettings(scopedPayload.settings, scopedPayload.scope);
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('emptyQuery.settings.saveFailed'),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'save failed');
    }
  },
);

export const fetchEmptyQueryArtifact = createAsyncThunk(
  'emptyQuery/fetchArtifact',
  async (scope = 'default', { extra, dispatch, rejectWithValue }) => {
    try {
      const { emptyQueryApi } = extra.serviceRegistry;
      if (!emptyQueryApi) {
        return rejectWithValue('emptyQueryApi not available on current transport');
      }
      return await emptyQueryApi.getArtifact(scope);
    } catch (err) {
      dispatch(enqueueErrorSnackbar(
        err.message || getLocalizedMessage('emptyQuery.artifact.loadFailed'),
        { variant: 'error' },
      ));
      return rejectWithValue(err?.message || 'load failed');
    }
  },
);

export const startEmptyQueryReindex = createAsyncThunk(
  'emptyQuery/startReindex',
  async (_, { extra, rejectWithValue }) => {
    const { emptyQueryApi } = extra.serviceRegistry;
    if (!emptyQueryApi) {
      return rejectWithValue('emptyQueryApi not available on current transport');
    }
    return emptyQueryApi.startReindex();
  },
);

export const searchEmptyQueryCategories = createAsyncThunk(
  'emptyQuery/searchCategories',
  async (query, { extra, rejectWithValue }) => {
    try {
      const { emptyQueryApi } = extra.serviceRegistry;
      if (!emptyQueryApi) {
        return rejectWithValue('emptyQueryApi not available on current transport');
      }
      return await emptyQueryApi.searchCategories(query);
    } catch (err) {
      return rejectWithValue(err?.message || 'search failed');
    }
  },
);

export const resolveEmptyQueryCategories = createAsyncThunk(
  'emptyQuery/resolveCategories',
  async (ids, { extra, rejectWithValue }) => {
    try {
      if (!ids || ids.length === 0) return [];
      const { emptyQueryApi } = extra.serviceRegistry;
      if (!emptyQueryApi) {
        return rejectWithValue('emptyQueryApi not available on current transport');
      }
      return await emptyQueryApi.resolveCategories(ids);
    } catch (err) {
      return rejectWithValue(err?.message || 'resolve failed');
    }
  },
);

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const DEFAULT_SETTINGS = {
  enabled: true,
  product_limit: 16,
  manual_term_suggestions: [],
  excluded_term_suggestions: [],
  manual_words: [],
  excluded_words: [],
  pinned_product_ids: [],
  excluded_product_ids: [],
  auto_fill_products: true,
  auto_fill_term_suggestions: true,
  auto_fill_categories: true,
  auto_fill_words: true,
  manual_category_ids: [],
  excluded_category_ids: [],
};

const initialState = {
  settings: DEFAULT_SETTINGS,
  settingsScope: 'default',
  settingsStatus: 'idle', // 'idle' | 'loading' | 'saving' | 'error'
  settingsError: null,
  artifact: null,
  artifactScope: 'default',
  artifactStatus: 'idle', // 'idle' | 'loading' | 'error'
  artifactError: null,
  pinnedCategoryObjects: [],       // resolved full objects for manual_category_ids
  categorySearchResults: [],       // results from last searchCategories call
  categorySearchStatus: 'idle',    // 'idle' | 'loading' | 'error'
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slice = createSlice({
  name: 'emptyQuery',
  initialState,
  reducers: {
    clearPinnedCategoryObjects: (state) => {
      state.pinnedCategoryObjects = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSettings
      .addCase(fetchEmptyQuerySettings.pending, (state, { meta }) => {
        state.settingsStatus = 'loading';
        state.settingsError = null;
        state.settingsScope = meta?.arg || 'default';
      })
      .addCase(fetchEmptyQuerySettings.fulfilled, (state, { payload, meta }) => {
        if (state.settingsScope !== (meta?.arg || 'default')) return;
        state.settingsStatus = 'idle';
        state.settings = payload;
      })
      .addCase(fetchEmptyQuerySettings.rejected, (state, { payload, meta }) => {
        if (state.settingsScope !== (meta?.arg || 'default')) return;
        state.settingsStatus = 'error';
        state.settingsError = payload || 'load failed';
      })
      // saveSettings
      .addCase(saveEmptyQuerySettings.pending, (state, { meta }) => {
        state.settingsStatus = 'saving';
        state.settingsError = null;
        state.settingsScope = meta?.arg?.scope || 'default';
      })
      .addCase(saveEmptyQuerySettings.fulfilled, (state, { payload, meta }) => {
        if (state.settingsScope !== (meta?.arg?.scope || 'default')) return;
        state.settingsStatus = 'idle';
        state.settings = payload;
      })
      .addCase(saveEmptyQuerySettings.rejected, (state, { payload, meta }) => {
        if (state.settingsScope !== (meta?.arg?.scope || 'default')) return;
        state.settingsStatus = 'error';
        state.settingsError = payload || 'save failed';
      })
      // fetchArtifact
      .addCase(fetchEmptyQueryArtifact.pending, (state, { meta }) => {
        state.artifactStatus = 'loading';
        state.artifactError = null;
        state.artifactScope = meta?.arg || 'default';
      })
      .addCase(fetchEmptyQueryArtifact.fulfilled, (state, { payload, meta }) => {
        if (state.artifactScope !== (meta?.arg || 'default')) return;
        state.artifactStatus = 'idle';
        state.artifact = payload;
      })
      .addCase(fetchEmptyQueryArtifact.rejected, (state, { payload, meta }) => {
        if (state.artifactScope !== (meta?.arg || 'default')) return;
        state.artifactStatus = 'error';
        state.artifactError = payload || 'load failed';
      })
      // searchCategories
      .addCase(searchEmptyQueryCategories.pending, (state) => {
        state.categorySearchStatus = 'loading';
      })
      .addCase(searchEmptyQueryCategories.fulfilled, (state, { payload }) => {
        state.categorySearchStatus = 'idle';
        state.categorySearchResults = payload || [];
      })
      .addCase(searchEmptyQueryCategories.rejected, (state) => {
        state.categorySearchStatus = 'error';
        state.categorySearchResults = [];
      })
      // resolveCategories
      .addCase(resolveEmptyQueryCategories.fulfilled, (state, { payload }) => {
        state.pinnedCategoryObjects = payload || [];
      });
  },
});

export default slice.reducer;
export const { clearPinnedCategoryObjects } = slice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectEmptyQuerySettings       = (state) => state.emptyQuery.settings;
export const selectEmptyQuerySettingsScope  = (state) => state.emptyQuery.settingsScope;
export const selectEmptyQuerySettingsStatus  = (state) => state.emptyQuery.settingsStatus;
export const selectEmptyQuerySettingsError   = (state) => state.emptyQuery.settingsError;
export const selectEmptyQueryArtifact        = (state) => state.emptyQuery.artifact;
export const selectEmptyQueryArtifactScope   = (state) => state.emptyQuery.artifactScope;
export const selectEmptyQueryArtifactStatus  = (state) => state.emptyQuery.artifactStatus;
export const selectEmptyQueryArtifactError   = (state) => state.emptyQuery.artifactError;
export const selectPinnedCategoryObjects     = (state) => state.emptyQuery.pinnedCategoryObjects;
export const selectCategorySearchResults     = (state) => state.emptyQuery.categorySearchResults;
export const selectCategorySearchStatus      = (state) => state.emptyQuery.categorySearchStatus;
